import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled,
  Checkbox,
  FormControlLabel,
  Divider,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as UIIcon,
  Check as CheckIcon,
  SettingsOutlined as MetaIcon,
  AccountTreeOutlined as SchemaIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { BaseButton, BaseTextField, BaseSelect, BaseCheckbox, BaseModal } from '../../../base';
import { SchemaBuilder } from './SchemaBuilder';
import { AllowedRole, SchemaPermissionConfig } from '../../../../../services/ConfigManagementService';
import RoleService, { Role } from '../../../../../services/RoleService';

// ─── Permission defaults ─────────────────────────────────────────
export const DEFAULT_PERMISSION: Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'> = {
  create: ['admin'],
  read: ['public'],
  update: ['admin'],
  delete: ['admin'],
};

// 'public' is always the first option (virtual role, not in DB)
const PUBLIC_ROLE_ENTRY = { key: 'public' as AllowedRole, label: 'Public', description: 'Anyone (no login required)' };

const ACTIONS: { key: keyof Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'>; label: string; color: string; bg: string }[] = [
  { key: 'create', label: 'Create', color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'read',   label: 'Read',   color: '#1565C0', bg: '#E3F2FD' },
  { key: 'update', label: 'Update', color: '#E65100', bg: '#FFF3E0' },
  { key: 'delete', label: 'Delete', color: '#C62828', bg: '#FFEBEE' },
];

// ─── Design tokens ───────────────────────────────────────────────
const FONT = '"Manrope", sans-serif';
const ACCENT = '#005e5d';
const ACCENT_DARK = '#004d4c';
const ACCENT_LIGHT = '#005e5d14';
const BORDER = '#E6E7EB';
const SURFACE = '#F2F3F5';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';

// ─── Custom Stepper connector ────────────────────────────────────
const AccentConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 18,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: BORDER,
    borderRadius: 1,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: ACCENT,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: ACCENT,
  },
}));

// ─── Custom step icon ────────────────────────────────────────────
interface StepIconProps {
  active?: boolean;
  completed?: boolean;
  icon: React.ReactNode;
}

const CustomStepIcon: React.FC<StepIconProps> = ({ active, completed, icon }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      fontWeight: 700,
      fontSize: '0.875rem',
      transition: 'all 0.2s',
      ...(completed
        ? { backgroundColor: ACCENT, color: '#fff' }
        : active
        ? { backgroundColor: ACCENT, color: '#fff', boxShadow: `0 0 0 4px ${ACCENT_LIGHT}` }
        : { backgroundColor: SURFACE, color: TEXT_TERTIARY, border: `2px solid ${BORDER}` }),
    }}
  >
    {completed ? <CheckIcon sx={{ fontSize: 18 }} /> : icon}
  </Box>
);

// ─── Interfaces ──────────────────────────────────────────────────
export interface EditingScheme {
  key: string;
  label?: string;
  schema: Record<string, any>;
  description: string;
  type: 'config' | 'collection';
  pinToSidebar?: boolean;
  isNew?: boolean;
}

export interface SchemaModalProps {
  open: boolean;
  editingScheme: EditingScheme | null;
  schemaEditorValue: string;
  saving: boolean;
  permission: Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'>;
  onClose: () => void;
  onKeyChange: (key: string) => void;
  onLabelChange: (label: string) => void;
  onTypeChange: (type: 'config' | 'collection') => void;
  onDescriptionChange: (description: string) => void;
  onPinToSidebarChange: (pinToSidebar: boolean) => void;
  onEditorChange: (value: string) => void;
  onPermissionChange: (permission: Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'>) => void;
  onSave: (overrides?: { key?: string; label?: string; description?: string }) => void;
}

const STEPS = [
  { label: 'Schema Metadata', icon: <MetaIcon sx={{ fontSize: 18 }} /> },
  { label: 'Schema Definition', icon: <SchemaIcon sx={{ fontSize: 18 }} /> },
  { label: 'Access Control',   icon: <LockIcon  sx={{ fontSize: 18 }} /> },
];

// ─── Component ───────────────────────────────────────────────────
export const SchemaModal: React.FC<SchemaModalProps> = ({
  open,
  editingScheme,
  schemaEditorValue,
  saving,
  permission,
  onClose,
  onKeyChange,
  onLabelChange,
  onTypeChange,
  onDescriptionChange,
  onPinToSidebarChange,
  onEditorChange,
  onPermissionChange,
  onSave,
}) => {
  const isEditing = editingScheme && !editingScheme.isNew;
  const [activeStep, setActiveStep] = useState(0);
  const [editorMode, setEditorMode] = useState<'visual' | 'json'>('visual');
  const [allRoles, setAllRoles] = useState<{ key: AllowedRole; label: string; description: string }[]>([PUBLIC_ROLE_ENTRY]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSchemaBuilderLoading, setIsSchemaBuilderLoading] = useState(true);

  // ── Local state for Step 1 text fields ─────────────────────────────────────
  // Prevents re-render cascade to ConfigManagementPage on every keystroke.
  // Values are synced to parent on blur (300 ms debounce) or before save.
  const [localKey, setLocalKey] = useState(editingScheme?.key ?? '');
  const [localLabel, setLocalLabel] = useState(editingScheme?.label ?? '');
  const [localDesc, setLocalDesc] = useState(editingScheme?.description ?? '');
  const metaDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMetaRef = useRef<{ key?: string; label?: string; desc?: string }>({});

  const flushMeta = useCallback(() => {
    if (metaDebRef.current) { clearTimeout(metaDebRef.current); metaDebRef.current = null; }
    const p = pendingMetaRef.current;
    pendingMetaRef.current = {};
    if (p.key !== undefined) onKeyChange(p.key);
    if (p.label !== undefined) onLabelChange(p.label);
    if (p.desc !== undefined) onDescriptionChange(p.desc);
  }, [onKeyChange, onLabelChange, onDescriptionChange]);

  const debouncedMeta = (patch: { key?: string; label?: string; desc?: string }) => {
    Object.assign(pendingMetaRef.current, patch);
    if (metaDebRef.current) clearTimeout(metaDebRef.current);
    metaDebRef.current = setTimeout(flushMeta, 300);
  };

  const handleSaveClick = useCallback(() => {
    if (metaDebRef.current) { clearTimeout(metaDebRef.current); metaDebRef.current = null; }
    const p = pendingMetaRef.current;
    pendingMetaRef.current = {};
    onSave({
      ...(p.key !== undefined ? { key: p.key } : {}),
      ...(p.label !== undefined ? { label: p.label } : {}),
      ...(p.desc !== undefined ? { description: p.desc } : {}),
    });
  }, [onSave]);

  // Sync local text state when the modal opens (new or different scheme)
  useEffect(() => {
    if (open) {
      setLocalKey(editingScheme?.key ?? '');
      setLocalLabel(editingScheme?.label ?? '');
      setLocalDesc(editingScheme?.description ?? '');
      pendingMetaRef.current = {};
      if (metaDebRef.current) { clearTimeout(metaDebRef.current); metaDebRef.current = null; }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load dynamic roles when the modal opens
  useEffect(() => {
    if (open) {
      setIsLoadingRoles(true);
      RoleService.getAllRoles()
        .then((roles: Role[]) => {
          setAllRoles([
            PUBLIC_ROLE_ENTRY,
            ...roles.map((r) => ({ key: r.name as AllowedRole, label: r.label, description: r.description ?? '' })),
          ]);
        })
        .catch(() => {}) // keep existing list on failure
        .finally(() => setIsLoadingRoles(false));
    }
  }, [open]);

  // Reset schema builder loading when entering step 1
  useEffect(() => {
    if (activeStep === 1) {
      setIsSchemaBuilderLoading(true);
      const timer = setTimeout(() => setIsSchemaBuilderLoading(false), 300);
      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  // Memoized handlers to prevent unnecessary re-renders
  const handleNext = useCallback(() => {
    flushMeta();
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  }, [flushMeta]);
  const handleBack = useCallback(() => setActiveStep((s) => Math.max(s - 1, 0)), []);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose]);

  const handleEditorModeChange = useCallback((_: React.MouseEvent<HTMLElement>, value: 'visual' | 'json') => {
    if (value) setEditorMode(value);
  }, []);

  const toggleRole = useCallback((action: keyof typeof permission, role: AllowedRole) => {
    const current = permission[action] as AllowedRole[];
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    onPermissionChange({ ...permission, [action]: next });
  }, [permission, onPermissionChange]);

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={isEditing ? `Edit Schema: ${localLabel || localKey}` : 'Create New Schema'}
      maxWidth="lg"
      showFooter={false}
      paperSx={{
        height: '90vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '14px',
        fontFamily: FONT,
        overflow: 'hidden',
      }}
      contentSx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
      }}
    >
      {/* ── Body ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Step 1 — Metadata */}
        {activeStep === 0 && (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <BaseTextField
              label="Schema Key"
              value={localKey}
              onChange={(e) => { setLocalKey(e.target.value); debouncedMeta({ key: e.target.value }); }}
              onBlur={flushMeta}
              placeholder="e.g., landingpage, header, etc."
              required
              helperText="Unique identifier stored in DB. Use lowercase with hyphens."
            />

            <BaseTextField
              label="Display Label"
              value={localLabel}
              onChange={(e) => { setLocalLabel(e.target.value); debouncedMeta({ label: e.target.value }); }}
              onBlur={flushMeta}
              placeholder="e.g., Landing Page, Blog Posts, etc."
              helperText="Human-readable name shown in UI. Defaults to key if left empty."
            />

            <BaseSelect
              label="Schema Type"
              value={editingScheme?.type || 'config'}
              onChange={(e) => onTypeChange((e.target as HTMLSelectElement).value as 'config' | 'collection')}
              options={[
                { label: 'Single Config — stores one configuration object', value: 'config' },
                { label: 'Collection — stores multiple items', value: 'collection' },
              ]}
            />

            <BaseTextField
              label="Description"
              multiline
              rows={3}
              value={localDesc}
              onChange={(e) => { setLocalDesc(e.target.value); debouncedMeta({ desc: e.target.value }); }}
              onBlur={flushMeta}
              placeholder="Brief description of what this schema is used for"
              helperText="Optional. Helps other admins understand the purpose of this schema."
            />

            {/* Pin to Sidebar card */}
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                p: 2,
                backgroundColor: editingScheme?.pinToSidebar ? ACCENT_LIGHT : SURFACE,
                transition: 'background-color 0.2s',
              }}
            >
              <BaseCheckbox
                label="Pin to Sidebar"
                checked={editingScheme?.pinToSidebar ?? false}
                onChange={(checked) => onPinToSidebarChange(checked)}
                description={'When enabled, a quick-access link appears in the sidebar under "Manager"'}
              />
            </Box>
          </Box>
        )}

        {/* Step 3 — Access Control */}
        {activeStep === 2 && (
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_SECONDARY, lineHeight: 1.6 }}>
              Configure which roles can perform each action on this schema's data. "Public" allows unauthenticated access.
            </Typography>

            {ACTIONS.map((action, idx) => (
              <Box key={action.key}>
                {idx > 0 && <Divider sx={{ mb: 2.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.4,
                      borderRadius: '6px',
                      backgroundColor: action.bg,
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: action.color }}>
                      {action.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_TERTIARY }}>
                    — who can {action.label.toLowerCase()} items
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 1,
                  }}
                >
                  {allRoles.map((role) => {
                    const checked = (permission[action.key] as AllowedRole[]).includes(role.key);
                    return (
                      <Box
                        key={role.key}
                        onClick={() => toggleRole(action.key, role.key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          p: 1.25,
                          border: `1px solid ${checked ? action.color + '55' : BORDER}`,
                          borderRadius: '8px',
                          backgroundColor: checked ? action.bg : SURFACE,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: action.color + '88', backgroundColor: action.bg },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          size="small"
                          disableRipple
                          sx={{
                            p: 0,
                            color: BORDER,
                            '&.Mui-checked': { color: action.color },
                          }}
                          onChange={() => {}}
                        />
                        <Box>
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', fontWeight: 600, color: checked ? action.color : TEXT_PRIMARY }}>
                            {role.label}
                          </Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_TERTIARY }}>
                            {role.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Step 2 — Schema Definition */}
        {activeStep === 1 && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: 3, py: 2.5, gap: 2 }}>
            {/* Mode toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9375rem', color: TEXT_PRIMARY }}>
                Field Definitions
              </Typography>
              <ToggleButtonGroup
                value={editorMode}
                exclusive
                size="small"
                onChange={handleEditorModeChange}
                sx={{
                  '& .MuiToggleButton-root': {
                    border: `1px solid ${BORDER}`,
                    fontSize: '12px',
                    fontFamily: FONT,
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: '5px 14px',
                    gap: 0.5,
                    color: TEXT_SECONDARY,
                    '&.Mui-selected': {
                      bgcolor: ACCENT,
                      color: '#fff',
                      border: `1px solid ${ACCENT}`,
                      '&:hover': { bgcolor: ACCENT_DARK },
                    },
                    '&:hover': { backgroundColor: SURFACE },
                  },
                }}
              >
                <ToggleButton value="visual">
                  <UIIcon sx={{ fontSize: 15 }} />
                  &nbsp;Visual Builder
                </ToggleButton>
                <ToggleButton value="json">
                  <CodeIcon sx={{ fontSize: 15 }} />
                  &nbsp;JSON
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Editor */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {editorMode === 'visual' ? (
                <Box
                  sx={{
                    height: '100%',
                    overflowY: 'auto',
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                    p: 1.5,
                    bgcolor: '#fafafa',
                    position: 'relative',
                  }}
                >
                  {isSchemaBuilderLoading ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <CircularProgress size={40} sx={{ color: ACCENT }} />
                      <Typography sx={{ fontSize: '0.875rem', color: TEXT_TERTIARY }}>
                        Loading Schema Builder...
                      </Typography>
                    </Box>
                  ) : (
                    <SchemaBuilder value={schemaEditorValue} onChange={onEditorChange} />
                  )}
                </Box>
              ) : (
                <Box sx={{ height: '100%', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
                  <Editor
                    height="100%"
                    defaultLanguage="json"
                    value={schemaEditorValue}
                    onChange={(v) => onEditorChange(v || '{}')}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      formatOnPaste: true,
                      formatOnType: true,
                      fontSize: 13,
                      fontFamily: '"Fira Code", "Cascadia Code", monospace',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Stepper (bottom) ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1.5, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, flexShrink: 0, bgcolor: '#fff' }}>
        <Stepper activeStep={activeStep} connector={<AccentConnector />} sx={{ px: 0 }}>
          {STEPS.map((step, index) => (
            <Step key={step.label} completed={index < activeStep}>
              <StepLabel
                StepIconComponent={(props) => (
                  <CustomStepIcon active={props.active} completed={props.completed} icon={step.icon} />
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontFamily: FONT,
                    fontWeight: index === activeStep ? 700 : 500,
                    fontSize: '0.8125rem',
                    color: index === activeStep ? ACCENT : index < activeStep ? TEXT_SECONDARY : TEXT_TERTIARY,
                    mt: 0.75,
                  },
                }}
                onClick={() => setActiveStep(index)}
                style={{ cursor: 'pointer' }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${BORDER}`,
          backgroundColor: SURFACE,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_TERTIARY, flex: 1 }}>
          Step {activeStep + 1} of {STEPS.length}
        </Typography>

        <BaseButton onClick={handleClose} variant="outlined" colorVariant="secondary">
          Cancel
        </BaseButton>

        {activeStep > 0 && (
          <BaseButton onClick={handleBack} variant="outlined" colorVariant="secondary">
            Back
          </BaseButton>
        )}

        {isLastStep ? (
          <BaseButton
            onClick={handleSaveClick}
            variant="contained"
            colorVariant="primary"
            disabled={saving}
            isLoading={saving}
            loadingLabel="Saving..."
          >
            Save Schema
          </BaseButton>
        ) : (
          <BaseButton onClick={handleNext} variant="contained" colorVariant="primary">
            Next
          </BaseButton>
        )}
      </Box>
    </BaseModal>
  );
};

export default React.memo(SchemaModal);
